import { StyledFormControl } from "Components/Shared/FormStyles";
import { PasswordInput } from "Components/Shared/Inputs/PasswordInput";
import type { TextFieldProps } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type Props<FormType extends FieldValues> = Omit<
  TextFieldProps,
  "type" | "inputEndAdornment"
> & {
  name: FieldPath<FormType>;
  label?: string;
  control: Control<FormType, object>;
  maxLength?: number;
  disabled?: boolean;
  valueTransformer?: (value: string) => unknown;
  disableHelperText?: boolean;
  // Suppresses the entire validation visual (red border + message) — for fields
  // whose validity is communicated elsewhere (e.g. a requirements checklist).
  isValidationHidden?: boolean;
};

export const FormPasswordInput = <T extends object>(props: Props<T>) => {
  const {
    name,
    control,
    maxLength,
    label,
    disabled,
    valueTransformer,
    disableHelperText,
    isValidationHidden,
    ...rest
  } = props;

  const outputTransform = (e: { target: { value: string } }) => {
    if (valueTransformer) {
      return valueTransformer(e.target.value);
    }

    // We need to convert empty string to null for required inputs for zod validation
    if (rest.required && e.target.value === "") {
      return null;
    }

    return e.target.value;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value, onBlur, ref },
        fieldState: { error },
      }) => (
        <StyledFormControl className={label ? "" : "no-label"}>
          <PasswordInput
            value={value === null || value === undefined ? "" : value}
            name={name.toString()}
            label={label}
            fieldError={isValidationHidden ? undefined : error}
            maxLength={maxLength}
            disableHelperText={disableHelperText || isValidationHidden}
            disabled={disabled}
            onBlur={onBlur}
            onChange={(event) => {
              const transformedValue = outputTransform(event);
              onChange(transformedValue);
            }}
            inputRef={ref}
            {...rest}
          />
        </StyledFormControl>
      )}
    />
  );
};
